import type { ISODateString, Loan, LoanEvent, LoanEventType } from '../../types';

/**
 * ============================================================================
 * VALIDATION
 * ============================================================================
 *
 * Plain, dependency-free validation functions (no external schema library
 * — the validation needs here are straightforward enough that a library
 * like zod would add a dependency for little benefit, and I can't verify
 * a new dependency installs cleanly in this handoff anyway). Each
 * function returns a list of human-readable error strings; an empty
 * array means valid. This keeps the validation logic trivially unit-
 * testable and framework-agnostic (usable from both the React forms and,
 * later, any import/restore validation in Phase 5).
 */

export type ValidationErrors = string[];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isValidIsoDate(value: string): value is ISODateString {
  if (!ISO_DATE_RE.test(value)) return false;
  const d = new Date(value + 'T00:00:00Z');
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

// ---------------------------------------------------------------------------
// Loan validation
// ---------------------------------------------------------------------------

export function validateLoanInput(input: Partial<Loan>): ValidationErrors {
  const errors: ValidationErrors = [];

  if (!input.lender || input.lender.trim().length === 0) {
    errors.push('Lender name is required.');
  }
  if (!isPositiveNumber(input.sanctionAmount)) {
    errors.push('Sanction amount must be a positive number.');
  }
  if (!isPositiveNumber(input.approvedAmount)) {
    errors.push('Approved amount must be a positive number.');
  }
  if (
    isPositiveNumber(input.sanctionAmount) &&
    isPositiveNumber(input.approvedAmount) &&
    input.approvedAmount! > input.sanctionAmount!
  ) {
    errors.push('Approved amount cannot exceed sanction amount.');
  }
  if (
    typeof input.originalAnnualRatePercent !== 'number' ||
    !Number.isFinite(input.originalAnnualRatePercent) ||
    input.originalAnnualRatePercent <= 0 ||
    input.originalAnnualRatePercent > 30
  ) {
    errors.push('Original interest rate must be a plausible percentage (0–30%).');
  }
  if (
    typeof input.originalTenureMonths !== 'number' ||
    !Number.isInteger(input.originalTenureMonths) ||
    input.originalTenureMonths <= 0 ||
    input.originalTenureMonths > 480
  ) {
    errors.push('Original tenure must be a whole number of months (1–480).');
  }
  if (!isPositiveNumber(input.originalEmiAtFullDisbursement)) {
    errors.push('Original EMI (at full disbursement) must be a positive number.');
  }
  if (
    typeof input.emiDayOfMonth !== 'number' ||
    !Number.isInteger(input.emiDayOfMonth) ||
    input.emiDayOfMonth < 1 ||
    input.emiDayOfMonth > 28
  ) {
    // Capped at 28 deliberately — EMI days of 29/30/31 behave inconsistently
    // across months (e.g. no Feb 30) and LIC HFL in practice uses an
    // early-month date; the user can revisit this if their real KFS
    // states otherwise.
    errors.push('EMI day of month must be between 1 and 28.');
  }
  if (input.loanStartDate && !isValidIsoDate(input.loanStartDate)) {
    errors.push('Loan start date is not a valid date.');
  }
  if (input.regularEmiStartDate && !isValidIsoDate(input.regularEmiStartDate)) {
    errors.push('Regular EMI start date is not a valid date.');
  }
  if (
    input.repaymentArrangement !== undefined &&
    !['unconfirmed', 'interest_only_then_regular', 'regular_from_start'].includes(input.repaymentArrangement)
  ) {
    errors.push('Repayment arrangement is not a recognised option.');
  }
  if (input.regularEmiAmount !== undefined && !isPositiveNumber(input.regularEmiAmount)) {
    errors.push('Regular EMI amount, if entered, must be a positive number.');
  }
  if (input.lenderReported !== undefined) {
    const lr = input.lenderReported;
    if (typeof lr !== 'object' || lr === null) {
      errors.push('Lender-reported figures are malformed.');
    } else {
      if (lr.asOfDate !== undefined && !isValidIsoDate(lr.asOfDate)) errors.push('Lender-reported "as of" date is not a valid date.');
      if (lr.contractualMaturityDate !== undefined && !isValidIsoDate(lr.contractualMaturityDate)) {
        errors.push('Lender-reported contractual maturity date is not a valid date.');
      }
      if (lr.instalmentAmount !== undefined && !isPositiveNumber(lr.instalmentAmount)) {
        errors.push('Lender-reported instalment amount, if entered, must be a positive number.');
      }
      if (lr.outstandingPrincipal !== undefined && !isNonNegativeNumber(lr.outstandingPrincipal)) {
        errors.push('Lender-reported outstanding principal, if entered, cannot be negative.');
      }
      if (lr.lastInstalmentAmount !== undefined && !isPositiveNumber(lr.lastInstalmentAmount)) {
        errors.push('Lender-reported last instalment amount, if entered, must be a positive number.');
      }
      if (lr.unpaidInstalments !== undefined && (!Number.isInteger(lr.unpaidInstalments) || lr.unpaidInstalments < 0)) {
        errors.push('Lender-reported unpaid instalments, if entered, must be a whole number.');
      }
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Event validation
// ---------------------------------------------------------------------------

/**
 * Validates the fields common to every event type: effectiveDate must be
 * present and a valid ISO date. Individual event-type validators call
 * this first, then add their own field checks.
 */
function validateBaseEvent(event: Partial<LoanEvent>): ValidationErrors {
  const errors: ValidationErrors = [];
  if (!event.effectiveDate) {
    errors.push('Effective date is required.');
  } else if (!isValidIsoDate(event.effectiveDate)) {
    errors.push('Effective date is not a valid date.');
  }
  if (!event.loanId) {
    errors.push('Internal error: event is missing a loanId.');
  }
  return errors;
}

/**
 * Validates a RepaymentConsequence sub-object, shared by rate_change,
 * prepayment (when principal_prepayment), and balance_correction events.
 *
 * PHASE 2: recording a consequence is no longer optional for these event
 * types (see each event type's doc comment in types/events.ts and the
 * call sites below) — this function is now called UNCONDITIONALLY
 * wherever it applies, not gated behind an "if present" check. It also
 * now validates the two new required fields: `primaryLever` and
 * `repaymentTermsEffectiveDate`.
 */
function validateRepaymentConsequence(consequence: unknown): ValidationErrors {
  const errors: ValidationErrors = [];
  if (typeof consequence !== 'object' || consequence === null) {
    errors.push('Repayment consequence details are required.');
    return errors;
  }
  const c = consequence as Partial<{
    newEmi: unknown;
    newRemainingTenureMonths: unknown;
    primaryLever: unknown;
    status: unknown;
    repaymentTermsEffectiveDate: unknown;
  }>;

  if (c.newEmi === undefined && c.newRemainingTenureMonths === undefined) {
    errors.push('Please provide a new EMI, a new remaining tenure, or both.');
  }
  if (c.newEmi !== undefined && !isPositiveNumber(c.newEmi)) {
    errors.push('New EMI must be a positive number.');
  }
  if (c.newRemainingTenureMonths !== undefined && !isPositiveNumber(c.newRemainingTenureMonths)) {
    errors.push('New remaining tenure must be a positive number of months.');
  }
  if (c.primaryLever !== 'emi' && c.primaryLever !== 'tenure') {
    errors.push('Please specify which value (EMI or remaining tenure) you set directly.');
  }
  if (c.status !== 'projected' && c.status !== 'actual_lic') {
    errors.push('Please specify whether this is a projection or an LIC-confirmed figure.');
  }
  if (!c.repaymentTermsEffectiveDate || typeof c.repaymentTermsEffectiveDate !== 'string') {
    errors.push('Repayment Terms Effective Date is required.');
  } else if (!isValidIsoDate(c.repaymentTermsEffectiveDate)) {
    errors.push('Repayment Terms Effective Date is not a valid date.');
  }
  return errors;
}

export function validateEvent(type: LoanEventType, event: Partial<LoanEvent>): ValidationErrors {
  const errors = validateBaseEvent(event);

  switch (type) {
    case 'disbursement': {
      const e = event as Partial<Extract<LoanEvent, { type: 'disbursement' }>>;
      if (!isPositiveNumber(e.amount)) {
        errors.push('Disbursement amount must be a positive number.');
      }
      break;
    }
    case 'rate_change': {
      const e = event as Partial<Extract<LoanEvent, { type: 'rate_change' }>>;
      if (
        typeof e.annualRatePercent !== 'number' ||
        !Number.isFinite(e.annualRatePercent) ||
        e.annualRatePercent <= 0 ||
        e.annualRatePercent > 30
      ) {
        errors.push('New interest rate must be a plausible percentage (0–30%).');
      }
      // PHASE 2: a rate change inherently affects repayment terms — the
      // consequence is always required, never optional.
      errors.push(...validateRepaymentConsequence(e.repaymentConsequence));
      break;
    }
    case 'prepayment': {
      const e = event as Partial<Extract<LoanEvent, { type: 'prepayment' }>>;
      if (!isPositiveNumber(e.amount)) {
        errors.push('Prepayment amount must be a positive number.');
      }
      if (e.paymentType !== 'principal_prepayment' && e.paymentType !== 'advance_emi') {
        errors.push('Payment type must be specified as either a principal prepayment or an advance EMI.');
      }
      if (e.paymentType === 'advance_emi' && e.repaymentConsequence) {
        errors.push('A repayment consequence does not apply to an advance EMI payment.');
      }
      // PHASE 2: a genuine principal prepayment inherently affects
      // repayment terms — the consequence is always required for
      // principal_prepayment (never optional), and structurally absent
      // for advance_emi.
      if (e.paymentType === 'principal_prepayment') {
        const c = e.repaymentConsequence as Partial<{ intent: unknown }> | undefined;
        if (
          !c ||
          (c.intent !== 'reduce_tenure' && c.intent !== 'reduce_emi' && c.intent !== 'both' && c.intent !== 'pending_lic_action')
        ) {
          errors.push('Please specify the intended treatment (reduce tenure, reduce EMI, both, or pending LIC action).');
        }
        errors.push(...validateRepaymentConsequence(e.repaymentConsequence));
      }
      break;
    }
    case 'balance_correction': {
      const e = event as Partial<Extract<LoanEvent, { type: 'balance_correction' }>>;
      if (!isNonNegativeNumber(e.licConfirmedBalance)) {
        errors.push('LIC-confirmed balance must be zero or a positive number.');
      }
      // PHASE 2: an LIC-confirmed balance correction is not optional
      // about its repayment consequence — always required.
      errors.push(...validateRepaymentConsequence(e.repaymentConsequence));
      break;
    }
    case 'charge': {
      const e = event as Partial<Extract<LoanEvent, { type: 'charge' }>>;
      if (!isPositiveNumber(e.amount)) {
        errors.push('Charge amount must be a positive number.');
      }
      if (!e.category) {
        errors.push('Please select a charge category.');
      }
      if (typeof e.financedIntoPrincipal !== 'boolean') {
        errors.push('Please specify whether this charge was financed into the loan principal.');
      }
      break;
    }
    case 'emi_date_change': {
      const e = event as Partial<Extract<LoanEvent, { type: 'emi_date_change' }>>;
      if (
        typeof e.newEmiDayOfMonth !== 'number' ||
        !Number.isInteger(e.newEmiDayOfMonth) ||
        e.newEmiDayOfMonth < 1 ||
        e.newEmiDayOfMonth > 28
      ) {
        errors.push('New EMI day of month must be between 1 and 28.');
      }
      break;
    }
  }

  return errors;
}
